/**
 * KMS 加解密配置文件
 * 用于管理需要加解密的 URL 和相关配置
 */

/**
 * 加密算法类型
 */
type KMSAlgorithm = 'AES' | 'SM4';

/**
 * 应用环境类型
 */
type AppEnv = 'dev' | 'test' | 'prod';

/**
 * 当前应用环境（可通过 setAppEnv 设置）
 * 默认值：'prod'
 */
let currentAppEnv: AppEnv = 'prod';

/**
 * 设置当前应用环境
 * 各子包应在初始化时调用此函数设置环境变量
 * @param env 应用环境：'dev' | 'test' | 'prod'
 * 
 */
export function setAppEnv(env: AppEnv): void {
  currentAppEnv = env;
}

/**
 * 获取当前应用环境
 * 如果未通过 setAppEnv 设置，则返回默认值 'prod'
 */
export const getAppEnv = (): AppEnv => {
  return currentAppEnv;
}

/**
 * KMS 配置
 */
interface KMSConfig {
  /** 是否启用 KMS 加解密 */
  enabled: boolean;
  /** 加密算法，默认 AES */
  algorithm: KMSAlgorithm;
  /** 是否在控制台输出详细日志 */
  verbose: boolean;
  /** 加密失败时是否继续发送请求（不加密） */
  continueOnEncryptError: boolean;
  /** 解密失败时是否继续处理响应（不解密） */
  continueOnDecryptError: boolean;
}

/**
 * 默认 KMS 配置
 */
const MY_KMS_CONFIG: KMSConfig = {
  enabled: true,
  algorithm: 'AES',
  verbose: true,
  continueOnEncryptError: true,
  continueOnDecryptError: true
};

export function setKMSConfigEnabled(enabled: boolean): void {
  MY_KMS_CONFIG.enabled = enabled;
}

/**
 * 需要加解密的 URL 模式列表（内部存储）
 * 支持字符串匹配或正则表达式匹配
 * 各子包通过 registerEncryptUrlPatterns 函数注册自己的 URL 模式
 */
const ENCRYPT_URL_PATTERNS: Array<string | RegExp> = [];

/**
 * 不需要加解密的 URL 例外列表（内部存储）
 * 优先级高于 ENCRYPT_URL_PATTERNS
 * 各子包通过 registerEncryptUrlExceptions 函数注册自己的例外规则
 */
const ENCRYPT_URL_EXCEPTIONS: Array<string | RegExp> = [];

/**
 * 注册需要加解密的 URL 模式
 * 各子包应在初始化时调用此函数注册自己的 URL 模式
 * @param patterns URL 模式列表，支持字符串或正则表达式
 */
export function registerEncryptUrlPatterns(patterns: Array<string | RegExp>): void {
  ENCRYPT_URL_PATTERNS.push(...patterns);
}

/**
 * 注册不需要加解密的 URL 例外规则
 * 各子包应在初始化时调用此函数注册自己的例外规则
 * @param exceptions URL 例外列表，支持字符串或正则表达式
 *
 */
export function registerEncryptUrlExceptions(exceptions: Array<string | RegExp>): void {
  ENCRYPT_URL_EXCEPTIONS.push(...exceptions);
}

/**
 * 判断 URL 是否需要加解密
 * @param url 请求 URL
 * @returns 是否需要加密
 */
export function shouldEncryptUrl(url: string): boolean {
  if (!url || !MY_KMS_CONFIG.enabled) {
    return false;
  }

  // 先检查例外列表
  const isException = ENCRYPT_URL_EXCEPTIONS.some(pattern => {
    if (typeof pattern === 'string') {
      return url.includes(pattern);
    }
    return pattern.test(url);
  });

  if (isException) {
    return false;
  }

  // 检查是否匹配需要加密的模式
  return ENCRYPT_URL_PATTERNS.some(pattern => {
    if (typeof pattern === 'string') {
      return url.includes(pattern);
    }
    return pattern.test(url);
  });
}

/**
 * 获取当前 KMS 配置
 */
export function getKMSConfig(): Readonly<KMSConfig> {
  return MY_KMS_CONFIG;
}

